'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonStep {
  id: string;
  traditional: {
    title: string;
    description: string;
    time: string;
    icon: string;
  };
  advisory: {
    title: string;
    description: string;
    time: string;
    icon: string;
  };
}

const ProblemSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setActiveStep(prev => (prev + 1) % comparisonSteps.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isHydrated]);

  const comparisonSteps: ComparisonStep[] = [
    {
      id: 'research',
      traditional: {
        title: 'Samodzielne poszukiwanie',
        description: 'Odwiedzanie wielu banków, porównywanie ofert, wypełnianie formularzy',
        time: '2-3 tygodnie',
        icon: 'MagnifyingGlassIcon',
      },
      advisory: {
        title: 'Jeden kontakt',
        description: 'Jeden formularz, jeden ekspert, dostęp do wszystkich banków',
        time: '1 dzień',
        icon: 'UserIcon',
      },
    },
    {
      id: 'application',
      traditional: {
        title: 'Złożone wnioski',
        description: 'Wielokrotne wypełnianie dokumentów, różne wymagania każdego banku',
        time: '1-2 tygodnie',
        icon: 'DocumentTextIcon',
      },
      advisory: {
        title: 'Profesjonalne przygotowanie',
        description: 'Ekspert przygotowuje dokumenty zgodnie z wymaganiami banków',
        time: '2-3 dni',
        icon: 'DocumentCheckIcon',
      },
    },
    {
      id: 'negotiation',
      traditional: {
        title: 'Brak negocjacji',
        description: 'Akceptacja standardowych warunków bez możliwości negocjacji',
        time: 'Brak',
        icon: 'XCircleIcon',
      },
      advisory: {
        title: 'Negocjacje eksperckie',
        description: 'Profesjonalne negocjacje warunków i oprocentowania',
        time: '1-2 dni',
        icon: 'ChatBubbleLeftRightIcon',
      },
    },
    {
      id: 'decision',
      traditional: {
        title: 'Niepewność wyboru',
        description: 'Trudność w porównaniu ofert i podjęciu najlepszej decyzji',
        time: 'Kilka dni',
        icon: 'QuestionMarkCircleIcon',
      },
      advisory: {
        title: 'Pewna decyzja',
        description: 'Otrzymujesz najlepszą ofertę z pełnym doradztwem',
        time: 'Natychmiast',
        icon: 'CheckCircleIcon',
      },
    },
  ];

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-background">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
              Dlaczego tracisz czas i pieniądze?
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Tradycyjny proces ubiegania się o finansowanie jest czasochłonny i często kończy się niekorzystnymi warunkami
            </p>
          </div>
          <div className="bg-muted/30 rounded-2xl h-96 animate-pulse" />
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-error/10 rounded-full mb-6">
            <Icon name="ExclamationTriangleIcon" size={20} variant="solid" className="text-error" />
            <span className="text-sm font-body font-medium text-error">
              Problem
            </span>
          </div>

          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Dlaczego traciłeś czas i pieniądze?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Tradycyjny proces ubiegania się o kredyt hipoteczny jest czasochłonny i często kończy się niekorzystnymi warunkami
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-card rounded-2xl shadow-card p-8 border-2 border-error/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-error/10 rounded-lg">
                <Icon name="XCircleIcon" size={24} variant="solid" className="text-error" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground">
                Tradycyjny sposób
              </h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Icon name="ClockIcon" size={20} variant="outline" className="text-error mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Długi proces (4-6 tygodni)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Wielokrotne wizyty w bankach, oczekiwanie na decyzje kredytowe
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="CurrencyDollarIcon" size={20} variant="outline" className="text-error mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Wyższe koszty kredytu
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Brak negocjacji, standardowe oprocentowanie kredytu hipotecznego
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="ExclamationCircleIcon" size={20} variant="outline" className="text-error mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Ryzyko odrzucenia
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Nieprawidłowo przygotowane dokumenty, brak doświadczenia
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="DocumentTextIcon" size={20} variant="outline" className="text-error mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Skomplikowane procedury
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Różne wymagania każdego banku, chaos dokumentacyjny
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm font-body font-semibold text-error text-center">
                Całkowity czas: 4-6 tygodni + wyższe koszty
              </p>
            </div>
          </div>

          <div className="bg-card rounded-2xl shadow-card p-8 border-2 border-success/20">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-12 h-12 bg-success/10 rounded-lg">
                <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success" />
              </div>
              <h3 className="text-xl font-headline font-bold text-foreground">
                Z naszym doradztwem
              </h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Icon name="BoltIcon" size={20} variant="outline" className="text-success mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Szybki proces (48 godzin)
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Jeden kontakt, kompleksowa obsługa, natychmiastowa odpowiedź
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="CurrencyDollarIcon" size={20} variant="outline" className="text-success mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Niższe koszty
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Profesjonalne negocjacje, najlepsze warunki rynkowe
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="ShieldCheckIcon" size={20} variant="outline" className="text-success mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Wysoka akceptowalność
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Profesjonalnie przygotowane wnioski, 15 lat doświadczenia
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Icon name="DocumentCheckIcon" size={20} variant="outline" className="text-success mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm font-body font-semibold text-foreground mb-1">
                    Prosta procedura
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Jeden formularz, ekspert zajmuje się wszystkim
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-sm font-body font-semibold text-success text-center">
                Całkowity czas: 48 godzin + oszczędności do 30%
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl shadow-card p-8">
          <h3 className="text-xl font-headline font-bold text-foreground mb-6 text-center">
            Porównanie procesu krok po kroku
          </h3>

          <div className="flex justify-center gap-2 mb-8">
            {comparisonSteps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(index)}
                className={`w-3 h-3 rounded-full transition-all duration-250 ${
                  activeStep === index ? 'bg-primary w-8' : 'bg-border'
                }`}
                aria-label={`Krok ${index + 1}`}
              />
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-error/5 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-error/10 rounded-lg">
                  <Icon 
                    name={comparisonSteps[activeStep].traditional.icon as any} 
                    size={20} 
                    variant="outline" 
                    className="text-error" 
                  />
                </div>
                <h4 className="text-lg font-headline font-bold text-foreground">
                  {comparisonSteps[activeStep].traditional.title}
                </h4>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {comparisonSteps[activeStep].traditional.description}
              </p>
              <div className="flex items-center gap-2">
                <Icon name="ClockIcon" size={16} variant="outline" className="text-error" />
                <span className="text-sm font-body font-semibold text-error">
                  {comparisonSteps[activeStep].traditional.time}
                </span>
              </div>
            </div>

            <div className="bg-success/5 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                  <Icon 
                    name={comparisonSteps[activeStep].advisory.icon as any} 
                    size={20} 
                    variant="outline" 
                    className="text-success" 
                  />
                </div>
                <h4 className="text-lg font-headline font-bold text-foreground">
                  {comparisonSteps[activeStep].advisory.title}
                </h4>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                {comparisonSteps[activeStep].advisory.description}
              </p>
              <div className="flex items-center gap-2">
                <Icon name="BoltIcon" size={16} variant="outline" className="text-success" />
                <span className="text-sm font-body font-semibold text-success">
                  {comparisonSteps[activeStep].advisory.time}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;